import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'
import { Identifier } from '@/components/ui/identifier'
import { Bidi } from '@/components/ui/bidi'

/* The assignment board — W-40, one of the three XL screens. Whether supervisors
   trust auto-assignment or override it every morning is decided here. */

export function LaneGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid items-start gap-3 [grid-template-columns:repeat(auto-fit,minmax(196px,1fr))]', className)}>
      {children}
    </div>
  )
}

export function AssignmentLane(
  { name, role, initials, avatarTone = 'var(--hk-secondary)', load, loadMax, loadLabel, children, className }:
  {
    name: ReactNode; role?: ReactNode; initials: string; avatarTone?: string
    load?: number; loadMax?: number; loadLabel?: ReactNode
    children: ReactNode; className?: string
  },
) {
  /* OVER BUDGET IS ITS OWN COLOUR, and the bar carries it as well as the number.
     A supervisor scanning six lanes reads the bars, not the figures — a lane that is
     over shows a full bar in the same blue as a lane that is exactly full, and the two
     are the opposite of each other. The colour is a workload token, deliberately not a
     room-state one: amber already means "dirty" on this product's screens. */
  const over = load !== undefined && loadMax !== undefined && load > loadMax

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-[10px] border border-border bg-card', className)}>
      <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
        <span
          aria-hidden
          className="flex size-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{ background: avatarTone, color: 'var(--hk-foreground)' }}
        >
          {initials}
        </span>
        <span className="min-w-0">
          <b className="block truncate text-[13px] font-semibold leading-tight"><Bidi>{name}</Bidi></b>
          {role && <span className="block truncate text-[11px] text-muted-fg"><Bidi>{role}</Bidi></span>}
        </span>
      </div>

      {load !== undefined && (
        <div className="flex flex-col gap-1.5 border-b border-border px-3 py-2">
          <span className={cn(
            'flex items-center justify-between text-[11px]',
            over ? 'font-semibold text-[var(--hk-load-over)]' : 'text-muted-fg',
          )}>
            {loadLabel}
          </span>
          <Progress
            value={load}
            max={loadMax ?? 100}
            tone={over ? 'var(--hk-load-over)' : 'var(--hk-load-ok)'}
            label="Workload"
          />
        </div>
      )}

      <div className="flex min-h-20 flex-col gap-1.5 p-2">{children}</div>
    </div>
  )
}

export function TaskCard(
  { room, detail, time, tone = 'var(--hk-primary)', className }:
  { room: string; detail?: ReactNode; time?: ReactNode; tone?: string; className?: string },
) {
  return (
    <article
      className={cn(
        'flex cursor-grab flex-col gap-1 rounded-[7px] border border-border bg-card px-2.5 py-2',
        'border-s-[3px] hover:shadow-[0_4px_10px_-5px_rgb(30_58_95/0.4)]',
        className,
      )}
      style={{ borderInlineStartColor: tone }}
    >
      <span className="flex items-center justify-between gap-2">
        <Identifier className="text-[13px] text-foreground">{room}</Identifier>
        {time && <span className="tnum text-[11px] text-muted-fg">{time}</span>}
      </span>
      {detail && <span className="truncate text-[11px] text-muted-fg"><Bidi>{detail}</Bidi></span>}
    </article>
  )
}

export function DropZone({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[7px] border-2 border-dashed border-[#7dd3fc] bg-[#f0f9ff] p-2.5 text-center text-[11.5px] text-[#0369a1] dark:bg-[#0c2a5e] dark:text-[#7dd3fc]">
      {children}
    </div>
  )
}
